<template>

	<div class="px-10 pt-5 pb-10">

		<div v-if="!layout.loading" class="w-full h-full dropzone-grid">

			<template v-if="showDropzone">

				<!-- Dropzone Area -->
				<div id="uploadComponent"
					v-bind="getRootProps()" 
					class="text-xs cursor-pointer rounded flex flex-col items-center justify-center"  
					:class="!layout.file ? 'col-span-12' : 'col-span-4'">

					<input id="uploadInput" v-bind="getInputProps()" />

					<UploadCloudIcon class="w-12 h-12 mt-1 rp-text-blue-1" />
					<h2 class="text-lg font-medium truncate mt-2">{{ $t('Upload files') }}</h2>

					<div class="mt-2 text-xs font-medium text-gray-800">
						{{ !isChrome 
							? (isDragActive 
								? $t('Drop the files here...') 
								: $t('Drag and drop files here or click to upload')
							)
							: $t('Click here to upload files')
						}}
					</div>

					<div class="flex flex-row gap-3 mt-5 justify-center">

						<button class="rp-btn flex items-center justify-center gap-2 px-4 py-2 text-white rp-bg-blue-3 whitespace-nowrap w-auto" @click.stop="triggerFileUpload">
							<UploadIcon class="w-5 h-5" />
							{{ !layout.file ? $t('Select Files') : $t('Files') }}
						</button>

						<button class="rp-btn flex items-center justify-center gap-2 px-4 py-2 text-white rp-bg-blue-3 whitespace-nowrap w-auto" @click.stop="triggerDirUpload">
							<FolderIcon class="w-5 h-5" />
							{{ !layout.file ? $t('Select Folder') : $t('Folder') }}
						</button>

					</div>

				</div>

				<!-- Zip Explanation -->
				<div v-if="!layout.file" class="col-span-12 p-4 text-gray-600 rounded border bg-gray-50">
					<div class="grid grid-cols-12 items-center">

						<div class="col-span-2">
							<img class="w-20 h-auto ml-5" :src="require(`@/assets/images/rosepetal/icon/zip.png`)" />
						</div>

						<div class="col-span-10">
							
							<!-- Multiclass / Multilabel -->
							<div v-if="layout.dataset.type == 'MULTICLASS' || layout.dataset.type == 'MULTILABEL'" class="text-xs py-3 w-full">
								<div class="font-medium text-sm mb-3 text-gray-700">{{$t('How to add multiple images to dataset using a Zip File')}}</div>
								<div>
									1. {{$t('Create a directory for each class tag Identifier')}}. 
									<div class="pl-3 mt-1">{{$t("Use directory name '0' for images you don't want to classify")}}.</div>   
								</div> 
								<div class="mt-2">2. {{$t('Insert the images in each directory according to their class')}}.</div> 
								<div v-if="layout.dataset.tags" class="ml-3 mt-1">
									<span >{{$t('Examples')}}: </span>
									<span v-if="layout.dataset.tags && Object.keys(layout.dataset.tags).length>1"><span class="pr-1 font-medium" v-for="(v,k,i) in layout.dataset.tags" :key="i"><span v-if="(i<4)">/{{v.id}}/image{{(i+1)}}.jpg,</span></span>...</span>
									<span v-else class="pr-1 font-medium">OK/image1.jpg, NOK/image1.jpg</span>
								</div> 
								<div class="mt-2">3. {{$t('Compress in Zip format and drag the file to the top uploading area')}}.</div>
							</div>

							<!-- Object Detection -->
							<div v-if="layout.dataset.type == 'imageObjectDetection'" class="text-xs py-3 w-full">
								<div class="font-medium text-sm mb-3">{{$t('How to add multiple images to object detection dataset using a Zip File')}}</div>
								<div>1. {{$t('Create a directory and insert all the images in the root')}}.</div> 
								<div>2. {{$t('Compress in Zip format')}}.</div>
								<div>3. {{$t('Drag the file to the top uploading area')}}.</div>
							</div>

							<!-- Object Detection -->
							<div v-if="layout.dataset.type == 'ANOMALY'" class="text-xs py-3 w-full">
								<div class="font-medium text-sm mb-3">{{$t('How to add multiple images to anomaly detection dataset using a Zip File')}}</div>
								<div>1. {{$t('Create a directory and insert all the images in the root')}}.</div> 
								<div>2. {{$t('Compress in Zip format')}}.</div>
								<div>3. {{$t('Drag the file to the top uploading area')}}.</div>
							</div>

						</div>

					</div>
				</div>

				<!-- Files Information -->
				<div v-else class="col-span-8 rounded border bg-gray-50">
					<div class="grid grid-cols-12 col-span-12 ml-3 ">

							<!-- Last file added -->
							<div class="col-span-12 px-5 py-4 text-sm">
								<span>
									<FileIcon class="w-4 h-4" style="vertical-align: top;" /> {{ $t('Last file added') }}: 
								</span>
								<div class="flex items-center pt-2 pb-1">
									<div class="w-10 h-10 flex-none image-fit rounded-full border flex items-center justify-center text-xs">{{ layout.file.name.toString().toUpperCase().split('.').pop() }}</div>
									<div class="ml-3 mr-auto">
										<span  class="font-medium">
											{{ layout.file.name.toString().toLowerCase().slice(0, 40) }} <span v-if="(layout.file.name.toString().toLowerCase().split('.')[0].length > 40)">...</span>
										</span>
										<div class="flex text-gray-600 truncate text-xs mt-0.5">
											<span v-if="layout.file.type">
												<span>{{$t('Type')}}: </span>
												<span>{{layout.file.type}}</span>
											</span>
											<span class="mx-1">•</span>
											<span v-if="layout.file.size">
												<span>{{$t('Size')}}: </span>
												<span>{{layout.file.size}}</span>
											</span>
										</div>
									</div>
								</div>
							</div>
							
							<!-- Valid Files -->
							<div v-if="hasValidFiles" class="col-span-12 pt-3 pb-3 border-t border-gray-200">

								<!-- VALID FILES -->
								<div v-if="layout.file?.filesCount && !layout.success && !layout.uploading" class="px-5 py-1 text-sm">
									<span><ImageIcon class="w-4 h-4" style="vertical-align: top;" /> {{ $t('Valid files') }}: </span>
									<div class="mt-1 ml-4">
										<span v-for="(f,k) in layout.file?.files" :key="k" class="pr-3">
											<span class="text-green-500">{{ (k) }} ({{ layout.file.files[k].length }})</span> 
										</span>
									</div>
								</div>
								
								<!-- VALID DIRECTORIES -->
								<div v-if="(Object.keys(layout.file?.dir).length && !layout.success && !layout.uploading)" class="px-5 py-1 text-sm">
									<span><FolderIcon class="w-4 h-4" style="vertical-align: top;" /> {{ $t('Tags directories') }}: </span>
									<div class="mt-2 ml-4">
										<span v-for="(d,k) in layout.file?.dir" :key="k" class="pr-2">
											<span class="text-green-500">{{ (k) }} ({{ (d.length) }}) <span v-if="k=='0'" class="text-green-500">*</span></span> 
										</span>
										<div v-if="Object.keys(layout.file?.dir).includes('0')" class="mt-2 __uploaddzmin font-italic font-normal ">* {{ $t("Use directory name '0' for unclassified images") }}</div>
									</div>
								</div>

								<!-- ANNOTATIONS -->
								<div v-if="layout.file?.annotationsCount && !layout.success && !layout.uploading" class="px-5 py-1 text-sm">
									<span><FileTextIcon class="w-4 h-4" style="vertical-align: top;" /> {{ $t('Annotations') }}: </span>
									<div class="mt-2 ml-4">
										<span v-for="(d,k) in layout.file?.annotations" :key="k" class="pr-2">
											<span v-if="d.length > 0" class="text-green-500">{{ k }} ({{ d.length }}) <span v-if="k=='0'" class="text-green-500">*</span></span>
										</span>
									</div>
								</div>

							</div>

							<!-- Invalid Files -->
							<div v-if="hasInvalidFiles" class="col-span-12 pt-3 pb-3 border-t border-gray-200">

								<!-- INVALID FILES -->
								<div v-if="(Object.keys(layout.file?.invalidFiles).length && !layout.uploading)" class="px-5 py-1 text-sm text-red-400 ">
									<span><AlertTriangleIcon class="w-4 h-4" style="vertical-align: top;" /> {{ $t('Invalid files') }}:</span>
									<div class="mt-1 ml-4">
										<span v-for="(d,k) in layout.file?.invalidFiles" :key="k" class="pr-3">
											<span>{{ (k) }} ({{ (d.length) }})</span> 
										</span>
									</div>
								</div>

								<!-- INVALID DIRECTORIES -->
								<div v-if="(Object.keys(layout.file?.invalidDir).length && !layout.uploading)" class="px-5 py-1 text-sm text-red-400">
									<span><FolderIcon class="w-4 h-4" style="vertical-align: top;" /> {{ $t('Invalid directories') }}:</span>
									<div class="mt-1 ml-4">
										<span v-for="(k) in layout.file?.invalidDir" :key="k" class="pr-3">
											<span>{{ k }}</span> 
										</span>
									</div>
								</div>

							</div>

					</div>
				</div>

				<!-- Warnings -->
				<div v-if="layout.file && layout.warnings.length" class="col-span-12 text-xs py-3">
					<div class="text-xs w-full px-1 py-2 text-center border text-gray-900 rounded font-normal">
						<div class="w-full text-truncate font-italic" v-for="warning in layout.warnings" :key="warning">
							{{ warning }}
						</div>
					</div>
				</div>

				<!-- Errors -->
				<div v-if="layout.file && layout.error" class="col-span-12 text-xs px-5 py-3 mt-3 flex items-center border text-red-400 rounded font-normal">
					<span class="w-full text-truncate font-italic">{{ layout.error }}.</span>
					<span class="py-2 px-5 rounded-full text-xs font-normal align-top text-center cursor-pointer float-right ml-auto bg-gray-700 text-white mr-2" v-if="(layout.file)" @click="initDropzone()">
						{{ $t('Reset') }}
					</span>
				</div>

				<!-- Cancel / Upload Buttons -->
				<div v-if="layout.file && !layout.error" class="col-span-12 flex items-center px-5 py-4">
					<Tippy tag="a" class="intro-x w-20 h-8 flex items-center justify-center rounded-full bg-theme-31 text-gray-900 ml-auto cursor-pointer py-5" v-if="(layout.file)" @click="initDropzone()"
						content="Cancel upload and back">{{$t('Cancel')}}</Tippy>
					<Tippy tag="a" class="intro-x w-32 h-8 flex items-center justify-center rounded-full bg-theme-17 text-white ml-2 cursor-pointer py-5" v-if="(layout.file && !layout.error)" @click="uploadFile()"
						content="Confirm and upload image">{{$t('Upload')}}</Tippy>
				</div>

			</template>

			<!-- Upload Status -->
			<div v-if="layout.uploading" class="intro-y col-span-12 flex flex-col justify-center items-center mt-0 pt-0 uploadingFull" >
							
				<div class="w-full text-center pt-5" style="background-color: #f1f2f1">
					
					<div class="text-lg" style="font-size: 26px !important">
						{{ layout.debug ? layout.debug : $t('Starting the upload, please wait') + "..." }}
					</div>

					<span v-if="layout.debug === ''" class="text-base text-red-600" style="line-height: 60px; font-size: 20px">
						{{ $t("Please do not close your browser") }}
					</span>

					<div class="mt-4">
						<button class="btn text-white py-2 px-4 rounded font-normal" 
							@click="$emit('closeDropzone')"
							:class="layout.debug === '' ? 'bg-green-200 cursor-not-allowed' : 'bg-green-500'"
							:disabled="layout.debug === ''">
							{{ $t('Continue') }}
						</button>
					</div>

				</div>

				<div class="w-1/2 text-center" :style="`background-image: url(${require(`@/assets/images/rosepetal/icon/loading.gif`)}); background-position: top center; background-size: contain; background-repeat: no-repeat; height: 60%`"></div>

			</div>

			<!-- Upload Completed -->
			<div v-else-if="layout.success" class="col-span-12 text-center">
				<div class="text-center  w-full mt-5">
					<img class="w-20 h-auto d-block mx-auto rounded" :src="require(`@/assets/images/rosepetal/icon/ok.png`)" />
				</div>
				<div class="items-center justify-center"></div>
				<div class="text-base mt-3 font-medium">{{$t('Upload Successful')}}</div>
				<div class="mt-2">{{$t('The images have been correctly incorporated into the dataset')}}</div>
				<div class="mb-10 mt-1 text-normal text-xs">{{$t('In a few moments you will have them available in data labeling')}}</div>
				<span class="px-5 py-3 bg-blue-500 cursor-pointer w-full font-normal text-white hover:bg-theme-10 mr-2" @click="initDropzone()">{{$t('New upload')}}</span> 
			</div>

			<!-- Upload Warning -->
			<div v-if="layout.warning">

				<div class="rp-overlay"></div>

				<div class="inset-0 flex items-center justify-center rp-dialogbox">
					<div class="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md relative animate-fade-in">
						
						<!-- Header -->
						<div class="flex items-center mb-4">
							<h2 class="text-xl font-semibold text-gray-800">
								{{ $t('Upload') }}
							</h2>
							
							<button
								class="ml-auto text-gray-400 hover:text-gray-600 transition-colors p-1"
								@click="layout.warning = false"
								aria-label="Close"
							>
								<XIcon class="w-6 h-6" />
							</button>
						</div>

						<!-- Message -->
						<div class="text-gray-600 text-sm leading-relaxed mb-4">
							<span class="text-red-500">{{ $t("Your browser version doesn't allow dragging files.") }}</span><br /><br />
							{{ $t('For uploading single images or ZIPs use:') }}
							<strong class="text-gray-800 font-medium">Select Files</strong><br />
							{{ $t('For uploading Folders use:') }}
							<strong class="text-gray-800 font-medium">Select Folder</strong>
						</div>

						<!-- Buttons -->
						<div class="flex flex-row gap-3">
							
							<button class="rp-btn w-1/2 flex items-center justify-center gap-2 px-4 py-2 text-white rp-bg-blue-3" @click.stop="triggerFileUpload()">
								<UploadIcon class="w-5 h-5" />
								{{ $t('Select Files') }}
							</button>
							
							<button class="rp-btn w-1/2 flex items-center justify-center gap-2 px-4 py-2 text-white rp-bg-blue-3" @click.stop="triggerDirUpload();">
								<FolderIcon class="w-5 h-5" />
								{{ $t('Select Folder') }}
							</button>

						</div>
						
					</div>
				</div>

			</div>

		</div>

		<div  v-else class="intro-y col-span-12 lg:col-span-12 flex flex-col justify-center items-center mt-0 uploadFileFull">
			<img class="w-1/2" :src="require(`@/assets/images/rosepetal/icon/loading.gif`)" />
		</div>

	</div>

</template>

<script setup>
import { onMounted, ref, computed }	from "vue";
import { useDropzone }				from "vue3-dropzone";
import JSZip        				from "jszip";
import lzstring 					from 'lz-string';
import Papa 						from 'papaparse';

import * as rosepetalModel  		from "rosepetal-model";
import config               		from '@/etc/rosepetal.json';

import { uploadBytes }				from "firebase/storage";

const $emit = defineEmits(['updateDataset']);

let $_firebase	= rosepetalModel.default._firebase;
let $dataset    = rosepetalModel.default.dataset;
let $helper     = rosepetalModel.default.helper;
let $image 		= rosepetalModel.default.image;

const props = defineProps({
	datasetId: {
		type: String,
		required: true,
	}
});

const isChrome = computed(() => {
	const ua = navigator.userAgent;
	const vendor = navigator.vendor;
	return /Chrome/.test(ua) && /Google Inc/.test(vendor);
});

const filteredFiles = computed(() => {
	const zip = layout.value.filesZip;
	if (!zip || !zip.files) return [];

	return Object.entries(zip.files).map(([name, file]) => ({ name, file })).filter(({ file }) => !file.dir);
});

const filteredAnnotations = computed(() => {
	const zip = layout.value.annotationsZip;
	if (!zip || !zip.files) return [];

	return Object.entries(zip.files).map(([name, file]) => ({ name, file })).filter(({ file }) => !file.dir);
});

const hasValidFiles		= computed(() => layout.value.file?.filesCount > 0 || layout.value.file?.annotationsCount > 0);
const hasInvalidFiles	= computed(() => Object.keys(layout.value.file?.invalidFiles).length > 0 || layout.value.file?.invalidDir.length > 0);

const showDropzone = ref(false);

const CONCURRENCY_LIMIT = 15;

const acceptedFiles 	= ['.jpg', '.jpeg', '.gif', '.png', '.bmp', '.webp', '.raw', '.ico', '.tiff', '.csv', '.txt', '.zip', '.json'];
const excludedPatterns 	= ['__MACOSX', '.DS_Store', '.db'];

const layout = ref({ 
	dataset:		{},
	loading: 		false,
	uploading: 		false,
	success: 		false,
	error: 			false,
	warning: 		false,
	file: 			false,
	files: 			[],
	warnings: 		[],
	filesZip: 		new JSZip(),
	annotationsZip:	new JSZip()
});


const triggerFileUpload = () => {
	layout.value.warning = false;
	const inputEl = document.getElementById('uploadInput');
	if (inputEl) {
		inputEl.removeAttribute('webkitdirectory');
		inputEl.removeAttribute('directory');
		inputEl.click();
	}
}

const triggerDirUpload = () => {
	layout.value.warning = false;
	const inputEl = document.getElementById('uploadInput');
	if (inputEl) {
		inputEl.setAttribute('webkitdirectory', '');
		inputEl.setAttribute('directory', '');
		inputEl.click();
	}
}


// ----- AUXILIAR FUNCTIONS -----

const timeOut = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const getTag = (filePath, datasetType) => {
	if (datasetType === 'imageObjectDetection' || datasetType === 'ANOMALY') return "0";

	const parts = filePath.split('/');
	const parentFolder = parts.at(-2) || "0";

	switch (datasetType) {
		case 'MULTICLASS': return parentFolder;
		case 'MULTILABEL': return parentFolder === "OK" ? "OK" : "0";
		default: return "0";
	}
}

const getExtension = (fileName) => {
	return fileName.includes('.') ? '.' + fileName.split('.').pop().toLowerCase() : '';
}

const isExcludedDir = (path) => {
	return excludedPatterns.some(pattern => path.includes(pattern));
}

const addFile = (tag, extension, fileObj) => {
	layout.value.file.dir[tag] = layout.value.file.dir[tag] || [];
	layout.value.file.files[extension] = layout.value.file.files[extension] || [];

	layout.value.file.dir[tag].push(fileObj);
	layout.value.file.files[extension].push(fileObj);
	layout.value.file.filesCount++;
}

const addAnnotation = (extension, fileObj) => {
	layout.value.file.annotations[extension].push(fileObj);
	layout.value.file.annotationsCount++;
}

const addInvalidDir = (filePath) => {
	if (!layout.value.file.invalidDir.includes(filePath)) {
		layout.value.file.invalidDir.push(filePath);
	}
}

const addInvalidFile = (extension, fileObj) => {
	layout.value.file.invalidFiles[extension] = layout.value.file.invalidFiles[extension] || [];
	layout.value.file.invalidFiles[extension].push(fileObj);
}


const initDropzone = async () => {
	showDropzone.value = true;

	Object.assign(layout.value, {
		loading: 		false,
		uploading: 		false,
		success: 		false,
		error: 			false,
		file: 			false,
		files: 			[],
		warnings: 		[],	
		filesZip: 		new JSZip(),
		annotationsZip:	new JSZip()
	});
}

const onDrop = async (acceptedFiles) => {
	const hasEmptyPath = acceptedFiles.some(file => file.webkitRelativePath === '' && !file.size);
	layout.value.warning = hasEmptyPath && isChrome.value;

	layout.value.loading = true;

	for (const file of acceptedFiles) {
		if (!file.size) continue;
		await processFile(file);
	}
	
	layout.value.loading = false;
}

const processFile = async (file) => {
	layout.value.file = { 
		name:         		file.name, 
		type:         		file.type, 
		size:         		$helper.formatBytes(file.size), 
		dir:          		layout.value.file?.dir 				|| {}, 
		files:        		layout.value.file?.files 			|| {}, 
		filesCount: 		layout.value.file?.filesCount 		|| 0,
		invalidFiles: 		layout.value.file?.invalidFiles 	|| {}, 
		invalidDir:   		layout.value.file?.invalidDir 		|| [], 
		annotations:  		layout.value.file?.annotations		|| { '.csv': [], '.txt': [], '.json': [] },
		annotationsCount:	layout.value.file?.annotationsCount || 0
	};

	switch (file.type) {
		case "application/zip":
		case "application/x-zip-compressed":
			await processZipFile(file);
			break;
		default:
			await processSingleFile(file);
			break;
	}

	validateFileProcessing();
}

const processZipFile = async (file) => {
	try {
		const zipData = await JSZip.loadAsync(file);
		const empty = Object.keys(zipData.files).length === 0;

		if (!empty) {
			for (const [filePath, fileData] of Object.entries(zipData.files)) {
				const pathParts	= filePath.split('/');
				const fileName 	= pathParts.pop();
				const extension = getExtension(fileName);

				if (fileData.dir) {
					if (isExcludedDir(filePath)) {
						addInvalidDir(`/${filePath}`);
					}
					continue;
				}

				const excludeFile = isExcludedDir(filePath) || !acceptedFiles.includes(extension) || layout.value.files.includes(fileName);
				const fileObj = { name: fileName, extension, file: fileData };

				if (excludeFile) {
					addInvalidFile(extension, fileObj);
				} else {
					if (['.csv', '.txt', '.json'].includes(extension)) {
						addAnnotation(extension, fileObj);
					} else {
						const tag = getTag(filePath, layout.value.dataset.type);
						addFile(tag, extension, fileObj);
					}
				}

				layout.value.files.push(fileName);
			}
		}
	} catch (error) {
		layout.value.error = "Error processing zip file";
	}
}

const processSingleFile = async (file) => {
	try {
		const extension 	= getExtension(file.name);
		const excludedFile 	= !acceptedFiles.includes(extension) || layout.value.files.includes(file.name);
		const excludedDir	= isExcludedDir(file.path);

		if (excludedFile || excludedDir) {
			addInvalidFile(extension, file);

			if (excludedDir) {
				const invalidPath = file.path.split('/').slice(0, -1).join('/');
				addInvalidDir(invalidPath);
			}

			layout.value.files.push(file.name);
			return;
		}

		await new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.onload = (e) => {
				const fileObj = {
					name: file.name,
					extension,
					file: new Blob([e.target.result], { type: file.type })
				};

				if (['.csv', '.txt', '.json'].includes(extension)) {
					addAnnotation(extension, fileObj);
				} else {
					const tag = getTag(file.path, layout.value.dataset.type, false);
					addFile(tag, extension, fileObj);
				}

				layout.value.files.push(file.name);
				resolve();
			};

			reader.onerror = reject;
			reader.readAsArrayBuffer(file);
		});
	} catch (error) {
		layout.value.error = "Error processing single file";
	}
}

const validateFileProcessing = () => {
	const { file, dataset, warnings } = layout.value;

	layout.value.error = false;

	const filesWarning = "Invalid or duplicated files will not be included in the dataset";
	const dirWarning = dataset.type === 'ANOMALY' ? "Anomaly detection dataset do not require tag directories" : "Object detection dataset do not require tag directories";

	const noValidFiles = !file.filesCount && !file.annotationsCount;
	if (noValidFiles) {
		layout.value.error = "The uploaded file is empty or there are no valid files";
		return;
	}

	if (Object.keys(file.invalidFiles).length || file.invalidDir.length) {
		if (!warnings.includes(filesWarning)) warnings.push(filesWarning);
	}

	const nonDefaultDirs = Object.keys(file.dir).filter(key => key !== "0");
	if ((dataset.type === 'imageObjectDetection' || dataset.type === 'ANOMALY') && nonDefaultDirs.length) {
		if (!warnings.includes(dirWarning)) warnings.push(dirWarning);
	}
}

const uploadFile = async () => {
	showDropzone.value = false;
		
	layout.value.uploading = true;
	layout.value.debug = "";

	const { file } = layout.value;
	const { dir, annotations, filesCount, annotationsCount } = file;

	const hasFiles = filesCount > 0;
	const hasAnnotations = annotationsCount > 0;

	try {
		if (hasFiles) addFilesToZip(dir);		
		if (hasAnnotations) addAnnotationsToZip(annotations);

		console.time("Manage upload");
		await manageUpload();
		console.timeEnd("Manage upload");

		layout.value.success = true;
	} catch (error) {
		console.error("Error uploading files:", error);
		layout.value.debug = error.message || "Unknown error during upload";
	}

	layout.value.uploading = false;
}

const addFilesToZip = (dirFiles) => {
	const { type } = layout.value.dataset;
	const isMultiClassOrLabel = type === 'MULTICLASS' || type === 'MULTILABEL';

	Object.entries(dirFiles).forEach(([dirKey, files = []]) => {
		const targetFolder = isMultiClassOrLabel ? layout.value.filesZip.folder(dirKey) : layout.value.filesZip;
		files.forEach(fileObj => {
			if (!fileObj) return;

			const { name: fileName, file  } = fileObj;
			const fileData = file?._data ?? file;

			if (fileName && fileData) {
				targetFolder.file(fileName, fileData, { binary: true });
			}
		});
	});
}

const addAnnotationsToZip = (annotations) => {
	Object.values(annotations).forEach(files => {
		files.forEach(file => {
			if (!file) return;
			const { name: fileName, file: fileData } = file;
			if (fileName && fileData) {
				layout.value.annotationsZip.file(fileName, fileData, { binary: true });
			}
		});
	});
}


const manageUpload = async () => {
	const filesCount 		= layout.value.file.filesCount;
	const annotationsCount	= layout.value.file.annotationsCount;

	const currentDate = $helper.getTimestampDate(new Date()).replace(/[\/,: ]+/g, '-');

	const configPath	= 'gs://' + config.firebaseConfig.projectId;
	const childPath 	= 'upload/manual/' + layout.value.dataset.id + "." + currentDate;

	if (filesCount) {
		await timeOut(2000);
		layout.value.debug = "Uploading images...";
		await manageUploadImages(configPath, childPath);
		layout.value.debug = "All images processed, finalizing...";
		await timeOut(2000);
	}
	
	if (annotationsCount) {
		layout.value.debug = "Uploading annotations...";
		await manageUploadAnnotations(configPath, childPath);
		layout.value.debug = "Annotations processed, finalizing...";
		await timeOut(2000);
	}

	await $dataset.updateProgress(layout.value.dataset.id, 'upload', 'finished');

	$emit("updateDataset", "images");
}

const manageUploadImages = async (configPath, childPath) => {
	let imgsProcessed = 0;
	const imgsToProcess = layout.value.file.filesCount;

	await $dataset.updateProgress(layout.value.dataset.id, 'upload', 'uploading', imgsToProcess);

	const updateInterval = setInterval(() => { 
		$dataset.updateProcessedCount(layout.value.dataset.id, 'upload', imgsProcessed); 
		layout.value.debug = `Processed ${imgsProcessed} / ${imgsToProcess} images...`;
	}, 1000);

	const uploadQueue = async () => {
		while (filteredFiles.value.length > 0) {
			const { name: fileName, file } = filteredFiles.value.pop();
			try {
				await $image.upload({
					file,
					fileName,
					dataset: layout.value.dataset,
					configPath,
					childPath
				});

				imgsProcessed++;
			} catch (err) {
				console.error(`Error uploading file ${fileName}:`, err);
			}
		}
	};

	const workers = Array.from({ length: CONCURRENCY_LIMIT }, uploadQueue);
	await Promise.all(workers);

	clearInterval(updateInterval);
	layout.value.debug = `Processed ${imgsProcessed} / ${imgsToProcess} images...`;
	
	await $dataset.updateProgress(layout.value.dataset.id, 'upload', 'finished');
}

/* const manageUploadAnnotations = async (configPath, childPath) => {
	let annotations = {};

	console.log("Processing annotations...");

	await Promise.all(filteredAnnotations.value.map(async ({ name, file }) => {
		const extension	= getExtension(name);
		const blob 		= await file.async('blob');
		const text 		= await blob.text();

		if (extension === '.json') {
			try {
				const jsonData = JSON.parse(text);
				const simpleFileName = name.split('/').pop();
				if (simpleFileName.trim() !== "") {
					if (!annotations[simpleFileName]) annotations[simpleFileName] = { tags: [] };

					const objectsById = {};

					for (const obj of jsonData.objects || []) {
						const id = obj.id || obj.name || 'unknown';
						if (!objectsById[id]) objectsById[id] = [];
						objectsById[id].push(obj);
					}

					for (const [id, objs] of Object.entries(objectsById)) {
						const partialJson = {
							version: 			jsonData.version,
							objects: 			objs,
							background: 		"",
							backgroundImage:	"",
							overlayImage: 		"",
							clipPath: 			"",
							width: 				jsonData.width || null,
							height: 			jsonData.height || null
						};

						const compressedPaths = objs.map(obj =>
							lzstring.compressToBase64(JSON.stringify(obj))
						);

						let maskUrl = "";
						const canvas = new fabric.Canvas(null);
						canvas.loadFromJSON(partialJson, () => {
							maskUrl = canvas.toDataURL();
						});

						annotations[simpleFileName].tags.push({
							type: 	id,
							mask: 	maskUrl,
							paths: 	compressedPaths
						});
					}
				}
			} catch (error) {
				console.error(`Error parsing JSON in file ${name}:`, error);
			}
			return;
		}

		const lines = text.split('\n');
		lines.forEach(line => {
			if (line.trim() === "") return;

			if (extension === '.csv') {
				const [set, image, tag, ...rest] = line.split(',');
				const imgName = image.trim().split('/').pop().replace(/^"+|"+$/g, '');
				console.log("rest:", rest);

				if (imgName) {
					const filteredData = rest.filter(item => item.trim() !== "");
					if (!annotations[imgName]) annotations[imgName] = { tags: [] };

					const tagName = String(tag).trim().replace(/^"(.*)"$/, "$1");
					annotations[imgName].tags.push({ type: tagName, data: filteredData });
				}
			}
			
			else if (extension === '.txt') {
				const [tag, ...data] = line.split(' ');
				const simpleFileName = name.split('/').pop();
				if (simpleFileName.trim() !== "") {
					if (!annotations[simpleFileName]) annotations[simpleFileName] = { tags: [] };
					annotations[simpleFileName].tags.push({ type: tag, data });
				}
			}
		});

		try {
			const uploadRef = $_firebase.getStorage(configPath, childPath + '/' + name);
			await uploadBytes(uploadRef, blob, { contentType: `text/${extension}` });
		} catch (error) {
			console.error('Error uploading', name, error);
		}
	}));

	console.log("Annotations processed:", annotations);

	const opt = { projectId: config.firebaseConfig.projectId };
	await $image.uploadAnnotations(annotations, layout.value.dataset, opt);
} */


const manageUploadAnnotations = async (configPath, childPath) => {
    let annotations = {};

    console.log("Processing annotations...");

    await Promise.all(filteredAnnotations.value.map(async ({ name, file }) => {
        const extension = getExtension(name);
        const blob = await file.async('blob');
        const text = await blob.text();

        if (extension === '.json') {
            try {
                const jsonData = JSON.parse(text);
                const simpleFileName = name.split('/').pop();
                if (simpleFileName.trim() !== "") {
                    if (!annotations[simpleFileName]) annotations[simpleFileName] = { tags: [] };

                    const objectsById = {};

                    for (const obj of jsonData.objects || []) {
                        const id = obj.id || obj.name || 'unknown';
                        if (!objectsById[id]) objectsById[id] = [];
                        objectsById[id].push(obj);
                    }

                    for (const [id, objs] of Object.entries(objectsById)) {
                        const partialJson = {
                            version: jsonData.version,
                            objects: objs,
                            background: "",
                            backgroundImage: "",
                            overlayImage: "",
                            clipPath: "",
                            width: jsonData.width || null,
                            height: jsonData.height || null
                        };

                        const compressedPaths = objs.map(obj =>
                            lzstring.compressToBase64(JSON.stringify(obj))
                        );

                        let maskUrl = "";
                        const canvas = new fabric.Canvas(null);
                        canvas.loadFromJSON(partialJson, () => {
                            maskUrl = canvas.toDataURL();
                        });

                        annotations[simpleFileName].tags.push({
                            type: id,
                            mask: maskUrl,
                            paths: compressedPaths
                        });
                    }
                }
            } catch (error) {
                console.error(`Error parsing JSON in file ${name}:`, error);
            }
        } else if (extension === '.csv') {
            await new Promise(resolve => {
                Papa.parse(text, {
                    skipEmptyLines: true,
                    complete: (results) => {
                        results.data.forEach(row => {
                            const [set, image, tag, ...rest] = row;
                            if (!image || !tag) return;

                            const imgName = image.trim().split('/').pop().replace(/^"+|"+$/g, '');

                            if (imgName) {
                                const filteredData = rest.filter(item => item.trim() !== "");
                                if (!annotations[imgName]) annotations[imgName] = { tags: [] };

                                const tagName = String(tag).trim().replace(/^"(.*)"$/, "$1");
                                annotations[imgName].tags.push({ type: tagName, data: filteredData });
                            }
                        });
                        resolve();
                    }
                });
            });
        } else if (extension === '.txt') {
            const lines = text.split('\n');
            lines.forEach(line => {
                if (line.trim() === "") return;
                const [tag, ...data] = line.split(' ');
                const simpleFileName = name.split('/').pop();
                if (simpleFileName.trim() !== "") {
                    if (!annotations[simpleFileName]) annotations[simpleFileName] = { tags: [] };
                    annotations[simpleFileName].tags.push({ type: tag, data });
                }
            });
        }

        try {
            const uploadRef = $_firebase.getStorage(configPath, childPath + '/' + name);
            await uploadBytes(uploadRef, blob, { contentType: `text/${extension.substring(1)}` });
        } catch (error) {
            console.error('Error uploading', name, error);
        }
    }));

    console.log("Annotations processed:", annotations);

    const opt = { projectId: config.firebaseConfig.projectId };
    await $image.uploadAnnotations(annotations, layout.value.dataset, opt);
};

const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

onMounted(async () => { 
	console.log("------ Dropzone mounted ------");
	layout.value.loading = true;

	if (props.datasetId) {
		layout.value.dataset = await $dataset.get(props.datasetId);
	}

	if (!layout.value.dataset.uploadResume || layout.value.dataset.uploadResume.status === 'finished') {
		await initDropzone();
	} else {
		layout.value.loading = false;
		layout.value.uploading = true;

		let uploadStatus = 'uploading';
		let totalImagesLocal = 0;
		
		while (uploadStatus !== 'finished') {
			try {
				const { uploadResume } = await $dataset.get(layout.value.dataset.id);
				uploadStatus 		= uploadResume.status;
				totalImagesLocal 	= uploadResume.totalImages;
				const processed 	= uploadResume.processedImages;
				
				layout.value.debug = uploadStatus === 'uploading'
					? `Processed ${processed} / ${totalImagesLocal} images...`
					: `Processed ${totalImagesLocal} / ${totalImagesLocal} images...`;
			} catch (err) {
				console.error('Error fetching dataset status:', err);
			}
			await timeOut(1000);
		}

		layout.value.debug = "All images processed, finalizing...";
		await timeOut(2000);

		await $dataset.updateProgress(layout.value.dataset.id, 'upload', 'finished');
		await initDropzone();
	}
});
</script>

<style>
.font-italic { 
	font-style: italic !important; 
}

.__uploaddzmin { 
	font-size: 11px; 
}

.uploadFileFull { 
	position: fixed !important; 
	left: 0 !important; 
	top: 0 !important; 
	z-index: 999 !important; 
	width: 100% !important; 
	height: 100vh !important; 
	background-color: #f1f2f1; 
}

.uploadingFull { 
	position: fixed !important; 
	left: 0 !important; 
	top: 0 !important; 
	z-index: 999 !important; 
	width: 100% !important; 
	height: 100vh !important; 
	background-color: #f1f2f1; 
}

#uploadComponent{ 
	width: 100%;
	min-height: 50vh;
	border: 2px dashed #edeaeaeb;
	background-color: #fff;
	padding: 20px;
	text-align: center;
}

.dropzone-grid {
	display: grid;
	grid-template-columns: repeat(12, minmax(0, 1fr));
	gap: 1rem;
}
</style>